import { Component, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  BodyClasses,
  ColumnDefinition,
  ColumnDisplayType,
  ColumnSizes,
  GridComponent,
  GridConfiguration,
  OrderByPipe,
  DialogContent,
  FilterType
} from 'components';
import { map, take } from 'rxjs/operators';
import { DialogService } from 'src/app/core/services/dialog.service';
import { PaymentCodesDialogComponent } from './payment-codes-dialog/payment-codes-dialog.component';
import { PaymentCodesService } from './payment-codes.service';
import { CodeWrapperService } from 'src/app/core/services/service-wrappers/code-wrapper.service';
import { NotificationService } from 'src/app/core/services/notification.service';
import { ActivatedRoute, Router } from '@angular/router';
import { ImplementationWrapperService } from '@core/services/service-wrappers/implementation-wrapper.service';
import { Store } from '@ngrx/store';
import { ImplementationState } from '../../implementation/state/implementation.state';
import { Observable, Subscription } from 'rxjs';
import { selectImplementation } from '../../implementation/state/implementation.selectors';
import * as ImplementationActions from '../../implementation/state/implementation.actions';
import { ImplementationDetailViewModel } from 'projects/data/src/lib/model/implementationDetailViewModel';
import { ImplementationViewModel } from 'projects/data/src/lib/model/implementationViewModel';
import { UploadCodesComponent } from '../upload-codes/upload-codes.component';
import { SessionStorageService } from '@core/services/session-storage.service';
import { UserDetailsViewModel } from 'data';

@Component({
  standalone: false,
  selector: 'app-payment-codes',
  templateUrl: './payment-codes.component.html',
  styleUrls: ['./payment-codes.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  providers: [PaymentCodesService]
})
export class PaymentCodesComponent implements OnInit, OnDestroy {
  @ViewChild(GridComponent) grid: GridComponent;
  orderPipe: OrderByPipe = new OrderByPipe();
  activeFilter: string = 'active';
  userInfo: UserDetailsViewModel;
  viewOnly = false;
  viewOnlyOptions = [{ id: 'view', label: 'View' }];
  overflowMenuOptions = [
    {
      id: 'edit',
      label: 'Edit'
    },
    {
      id: 'activate',
      label: 'Activate',
      field: 'inactive',
      condition: true
    },
    {
      id: 'inactivate',
      label: 'Inactivate',
      field: 'inactive',
      condition: false
    }
  ];

  private columnDefinitions: ColumnDefinition[] = [
    {
      id: 'actions',
      displayKey: 'actions',
      displayType: ColumnDisplayType.actionList,
      columnSize: ColumnSizes.fixedTiny,
      bodyClasses: [BodyClasses.alignRight],
      displayOrder: 0,
      headerText: '',
      overflowMenuOptions: this.overflowMenuOptions,
      overflowMenuClicked: (value, data) => {
        if (value === 'delete') {
          this.deleteEntry(data);
        } else if (value === 'edit') {
          this.openPaymentCodeModal(data.id);
        } else if (value === 'view') {
          this.openPaymentCodeModal(data.id, true);
        } else if (value === 'inactivate') {
          this.setInactive(data, 'Inactivate');
        } else if (value === 'activate') {
          this.setInactive(data, 'Activate');
        }
      }
    },
    {
      id: 'code',
      displayKey: 'code',
      headerText: 'Code',
      displayOrder: 1,
      columnSize: ColumnSizes.narrow,
      filterable: true,
      sortable: true,
      sticky: true
    },
    {
      id: 'description',
      displayKey: 'name',
      headerText: 'Description',
      columnSize: ColumnSizes.superWide,
      filterable: true,
      sortable: true,
      displayOrder: 2
    }
  ];
  gridConfig: GridConfiguration = {
    columnDefinitions: this.columnDefinitions,
    displayColumns: this.orderPipe.transform(this.columnDefinitions, 'displayOrder').map((x) => x.id)
  };

  dataSource: PaymentCodesService;
  constructor(
    private paymentCodesService: PaymentCodesService,
    public dialog: MatDialog,
    private deleteService: CodeWrapperService,
    private dialogService: DialogService,
    private notificationService: NotificationService,
    private activatedRoute: ActivatedRoute,
    private implementationService: ImplementationWrapperService,
    private router: Router,
    private store: Store<ImplementationState>,
    private sessionStorageService: SessionStorageService
  ) {
    this.dataSource = paymentCodesService;
    this.dataSource.activeFilter = 'active';
    this.userInfo = this.sessionStorageService.getItem('user-info');
    this.viewOnly =
      this.userInfo?.userPermissions?.find((x) => x.permissionType == 'SystemMaintenance')?.accessType !== 'Update';
    if (this.viewOnly) {
      this.columnDefinitions[0].overflowMenuOptions = this.viewOnlyOptions;
    }
  }

  addPaymentCode() {
    this.openPaymentCodeModal(null);
  }

  openPaymentCodeModal(paymentCodeId: string, viewOnly = false) {
    let activePmt = [];
    let activeCodes = [];

    this.deleteService
      .apiV1CodeCodetypePost('PaymentCode', { pageSize: 99999, generalSearch: '' })
      .pipe(
        map((x: any) => {
          return x;
        })
      )
      .subscribe((data) => {
        activePmt = data.data.filter((prov) => prov.id !== paymentCodeId);

        if (activePmt !== null) {
          activePmt.forEach((x) => {
            activeCodes.push(x.code.toString());
          });
        }

        const dialog = this.dialog.open(PaymentCodesDialogComponent, {
          disableClose: true,
          autoFocus: false,
          data: { paymentCodeId, viewOnly, activeCodes }
        });

        dialog.afterClosed().subscribe((result) => {
          if (result) {
            this.grid.loadData();
          }
        });
      });
  }

  deleteEntry(data) {
    const modalContent: DialogContent = {
      header: 'Delete Payment Code',
      body: `Are you sure you want to delete "${data.name}"? This cannot be undone.`,
      cancelButtonText: 'Cancel',
      OKButtonText: 'Delete'
    };
    this.dialogService.showConfirm(modalContent).subscribe((result) => {
      if (result) {
        this.deleteService
          .apiV1CodeCodetypeDeleteIdDelete('PaymentCode', data.id)
          .pipe(
            map((x: any) => x),
            take(1)
          )
          .subscribe((deleteResult) => {
            this.grid.loadData();
            this.notificationService.notifyNow('Payment Code Deleted');
          });
      }
    });
  }

  uploadCodes() {
    const dialog = this.dialog.open(UploadCodesComponent, {
      disableClose: true,
      autoFocus: false,
      height: '600px',
      width: '600px',
      data: { codeType: 'PaymentCode' }
    });

    dialog.afterClosed().subscribe((result) => {
      this.grid.loadData();
    });
  }

  setInactive(data, type) {
    const modalContent: DialogContent = {
      header: `${type} Payment Code`,
      body: `Are you sure you want to ${type} "${data.name}"?`,
      cancelButtonText: 'Cancel',
      OKButtonText: `${type}`
    };
    this.dialogService.showConfirm(modalContent).subscribe((result) => {
      if (result) {
        this.deleteService
          .apiV1CodeCodetypePatchIdPatch('PaymentCode', data.id, [
            {
              value: type === 'Inactivate' ? true : false,
              path: 'inactive',
              op: 'add'
            }
          ])
          .pipe(
            map((x: any) => x),
            take(1)
          )
          .subscribe((x) => {
            this.grid.loadData();
          });
      }
    });
  }

  public isImplementation: boolean = false;
  public gonext: boolean = false;
  public itemCount: number = 0;
  private activatedRouteSub: Subscription;
  private itemCountSub: Subscription;
  private implementation$: Observable<ImplementationViewModel> = this.store.select(selectImplementation);
  private implementationSub: Subscription;
  private implementation: ImplementationViewModel = null;
  private implementationDetail: ImplementationDetailViewModel = null;
  private nextImplementationDetail: ImplementationDetailViewModel = null;
  private STEP_CODE = 'payment-codes';

  implementationSubscribe() {
    this.activatedRouteSub = this.activatedRoute.data?.subscribe((data) => {
      this.isImplementation = data.implementation;

      if (this.isImplementation) {
        this.itemCountSub = this.paymentCodesService.itemCountSubject.subscribe((c) => (this.itemCount = c));
        this.implementationSub = this.implementation$.subscribe((i) => {
          this.implementation = i;
          this.implementationDetail = this.implementation.details.find(
            (d) => d.implementationStepCode == this.STEP_CODE
          );
          this.nextImplementationDetail = this.implementation.details.find(
            (d) => d.order == this.implementationDetail.order + 1
          );
        });
      }
    });
    this.store.dispatch(ImplementationActions.updateImplementationStep({ stepCode: this.STEP_CODE }));
  }

  implementationUnsubscribe() {
    if (this.isImplementation) {
      this.activatedRouteSub.unsubscribe();
      this.itemCountSub.unsubscribe();
      this.implementationSub.unsubscribe();
    }
  }

  next(): void {
    if (this.implementationDetail.status == 'InProgress') {
      this.gonext = true;
      this.implementationService.apiV1ImplementationCompleteStepCodePost(this.STEP_CODE).subscribe(
        () => {
          this.gonext = false;
          this.router.navigateByUrl('/implementation/' + this.nextImplementationDetail.implementationStepCode);
        },
        () => (this.gonext = false)
      );
    } else {
      this.router.navigateByUrl('/implementation/' + this.nextImplementationDetail.implementationStepCode);
    }
  }

  ngOnInit(): void {
    this.implementationSubscribe();
  }
  ngOnDestroy(): void {
    this.implementationUnsubscribe();
  }
  showInactiveChanges(event) {
    this.activeFilter = event.checked ? 'active' : 'inactive';
    this.dataSource.activeFilter = this.activeFilter;
    this.grid.loadData();
  }
}
